module Client.Admin.UserInfo

open Client
open Elmish
open Shared.Admin.UserInfo
open Illy.SafeStack.Client
open Shared
open Fable.React
open Fable.React.Props
open Illy.SafeStack.Client.jQueryUI

type DisplayedSection =
    | NoSection
    | LoggedInUsers
    | InactiveUsers
    | ActiveUsers
    | Audit

type Model =
    {
        Displayed: DisplayedSection
        LoggedInUsers: UserInfoSections.LoggedInUsers.Model
        InactiveUsers: UserInfoSections.InactiveUsers.Model
        ActiveUsers: UserInfoSections.ActiveUsers.Model
        Audit: UserInfoSections.Audits.Model
    }

type Msg =
    | LoggedInUsersMsg of UserInfoSections.LoggedInUsers.Msg
    | InactiveUsersMsg of UserInfoSections.InactiveUsers.Msg
    | ActiveUsersMsg of UserInfoSections.ActiveUsers.Msg
    | AuditMsg of UserInfoSections.Audits.Msg
    | DownloadLoggedInUsers
    | DownloadActiveUsers
    | DownloadDisabledInUsers
    | DownloadInactiveUsers
    | Download50MostActiveUsers
    | FileDownloaded of unit
    | ApiError of ApiError
    | CloseOverlay
    | SwitchSection of DisplayedSection

type Event =
    | ApiError of ApiError
    | CloseOverlay

let init() =
    let liuM, liuCmd = UserInfoSections.LoggedInUsers.init()
    let inaM, inaCmd = UserInfoSections.InactiveUsers.init()
    let mauM, mauCmd = UserInfoSections.ActiveUsers.init()
    let aM, aCmd = UserInfoSections.Audits.init()

    {
        Displayed = NoSection
        LoggedInUsers = liuM
        InactiveUsers = inaM
        ActiveUsers = mauM
        Audit = aM
    }, Cmd.batch [
        Cmd.map LoggedInUsersMsg liuCmd
        Cmd.map InactiveUsersMsg inaCmd
        Cmd.map ActiveUsersMsg mauCmd
        Cmd.map AuditMsg aCmd
    ]

let dispatchLoggedInUsersMsg model msg =
    let m, eff = UserInfoSections.LoggedInUsers.update msg model.LoggedInUsers
    match eff with
    | SideEffect.Cmd c -> { model with LoggedInUsers = m }, Cmd.map LoggedInUsersMsg c |> SideEffect.Cmd
    | SideEffect.Event ev ->
        match ev with
        | UserInfoSections.LoggedInUsers.Event.ApiError err ->
            { model with LoggedInUsers = m }, err |> ApiError |> SideEffect.Event
        | UserInfoSections.LoggedInUsers.Event.CloseOverlay ->
            { model with Displayed = NoSection }, SideEffect.none

let dispatchInactiveUsersMsg model msg =
    let m, eff = UserInfoSections.InactiveUsers.update msg model.InactiveUsers
    match eff with
    | SideEffect.Cmd c -> { model with InactiveUsers = m }, Cmd.map InactiveUsersMsg c |> SideEffect.Cmd
    | SideEffect.Event ev ->
        match ev with
        | UserInfoSections.InactiveUsers.Event.ApiError err ->
            { model with InactiveUsers = m }, err |> ApiError |> SideEffect.Event
        | UserInfoSections.InactiveUsers.Event.CloseOverlay ->
            { model with Displayed = NoSection }, SideEffect.none

let dispatchActiveUsersMsg model msg =
    let m, eff = UserInfoSections.ActiveUsers.update msg model.ActiveUsers
    match eff with
    | SideEffect.Cmd c -> { model with ActiveUsers = m }, Cmd.map ActiveUsersMsg c |> SideEffect.Cmd
    | SideEffect.Event ev ->
        match ev with
        | UserInfoSections.ActiveUsers.Event.ApiError err ->
            { model with ActiveUsers = m }, err |> ApiError |> SideEffect.Event
        | UserInfoSections.ActiveUsers.Event.CloseOverlay ->
            { model with Displayed = NoSection }, SideEffect.none

let dispatchAuditMsg model msg =
    let m, eff = UserInfoSections.Audits.update msg model.Audit
    match eff with
    | SideEffect.Cmd c -> { model with Audit = m }, Cmd.map AuditMsg c |> SideEffect.Cmd
    | SideEffect.Event ev ->
        match ev with
        | UserInfoSections.Audits.Event.ApiError err ->
            { model with Audit = m }, err |> ApiError |> SideEffect.Event
        | UserInfoSections.Audits.Event.CloseOverlay ->
            { model with Displayed = NoSection }, SideEffect.none

let update msg model =
    match msg with
    | Msg.CloseOverlay -> model, CloseOverlay |> SideEffect.Event
    | Msg.ApiError err -> model, err |> ApiError |> SideEffect.Event
    | LoggedInUsersMsg msg -> dispatchLoggedInUsersMsg model msg
    | InactiveUsersMsg msg -> dispatchInactiveUsersMsg model msg
    | ActiveUsersMsg msg -> dispatchActiveUsersMsg model msg
    | AuditMsg msg -> dispatchAuditMsg model msg
    | DownloadLoggedInUsers ->
        model, Api.downloadFile SecuredRoute.Admin (fun x -> x.downloadLoggedInUsers) () FileDownloaded Msg.ApiError |> SideEffect.Cmd
    | DownloadActiveUsers ->
        model, Api.downloadFile SecuredRoute.Admin (fun x -> x.downloadActiveUsers) () FileDownloaded Msg.ApiError |> SideEffect.Cmd
    | Download50MostActiveUsers ->
        model, Api.downloadFile SecuredRoute.Admin (fun x -> x.download50MostActiveUsers) () FileDownloaded Msg.ApiError |> SideEffect.Cmd
    | DownloadDisabledInUsers ->
        model, Api.downloadFile SecuredRoute.Admin (fun x -> x.downloadDisabledUsers) () FileDownloaded Msg.ApiError |> SideEffect.Cmd
    | DownloadInactiveUsers ->
        model, Api.downloadFile SecuredRoute.Admin (fun x -> x.downloadInactiveUsers) () FileDownloaded Msg.ApiError |> SideEffect.Cmd
    | FileDownloaded _ -> model, SideEffect.none
    | SwitchSection section -> 
        match section with
        | InactiveUsers ->
            let inMod, inCmd = UserInfoSections.InactiveUsers.showGrid model.InactiveUsers
            {
                model with
                    Displayed = section
                    InactiveUsers = inMod
            }, Cmd.map InactiveUsersMsg inCmd |> SideEffect.Cmd
        | ActiveUsers ->
            let inMod, inCmd = UserInfoSections.ActiveUsers.showGrid model.ActiveUsers
            {
                model with
                    Displayed = section
                    ActiveUsers = inMod
            }, Cmd.map ActiveUsersMsg inCmd |> SideEffect.Cmd
        | Audit ->
            let inMod, inCmd = UserInfoSections.Audits.showGrid model.Audit
            {
                model with
                    Displayed = section
                    Audit = inMod
            }, Cmd.map AuditMsg inCmd |> SideEffect.Cmd
        | _ ->
            { model with Displayed = section }, SideEffect.none

type Section =
    {
        Name: string
        Download: Option<Msg>
        OnScreen: Option<string * DisplayedSection>
    }

let reports model =
    [
        {
            Name = "Details of all active users on the system."
            Download = Some DownloadActiveUsers
            OnScreen = None
        }
        {
            Name = "Details of disabled users on the system."
            Download = Some DownloadDisabledInUsers
            OnScreen = None
        }
        {
            Name = "Number of users currently online."
            Download = Some DownloadLoggedInUsers
            OnScreen = Some (model.LoggedInUsers.Users.Length.ToString(), LoggedInUsers)
        }
        {
            Name = "Users not logged in for 28 days."
            Download = Some DownloadInactiveUsers
            OnScreen = Some (model.InactiveUsers.Count.ToString(), InactiveUsers)
        }
        {
            Name = "The 50 most active users."
            Download = Some Download50MostActiveUsers
            OnScreen = Some ("Show", ActiveUsers)
        }
        {
            Name = "Audit log"
            Download = None
            OnScreen = Some ("Show", Audit)
        }
    ]

let view (model: Model) dispatch =
    [
        div [ Id "user-info" ] [   
            yield! jQueryUI.modal
                "User Info"
                2000
                Msg.CloseOverlay
                dispatch
                (Some 700)
                [
                    h4 [] [ str "REPORTS" ]
                    div [] [
                        for report in reports model do
                            yield div [ ClassName "report" ] [
                                yield Standard.label [] [ str report.Name ]
                                
                                match report.Download with
                                | None -> ()
                                | Some dl -> yield a [ ClassName "download"; OnClick (fun _ -> dispatch dl) ] [
                                    FontAwesome.light "file-csv" [ FontAwesome.Size FontAwesome.X2 ] []
                                ]

                                match report.OnScreen with
                                | None -> ()
                                | Some (txt, sc) -> 
                                    yield a [ OnClick (fun _ -> dispatch (SwitchSection sc)) ] [ str txt ]
                            ]
                    ]
                ]
            
            match model.Displayed with
            | NoSection -> ()
            | LoggedInUsers ->
                yield! UserInfoSections.LoggedInUsers.view model.LoggedInUsers (LoggedInUsersMsg >> dispatch)
            | InactiveUsers ->
                yield! UserInfoSections.InactiveUsers.view model.InactiveUsers (InactiveUsersMsg >> dispatch)
            | ActiveUsers ->
                yield! UserInfoSections.ActiveUsers.view model.ActiveUsers (ActiveUsersMsg >> dispatch)
            | Audit ->
                yield! UserInfoSections.Audits.view model.Audit (AuditMsg >> dispatch)
        ]
    ]